// Prototype dock: the right column's rail hosts a Context⇄Chat switch.
// In Chat mode the rail shows the chat's session tabs and the column
// body shows the chat. The chat is one ChatConsole instance whose state
// we don't want to lift, so it publishes two things across this seam:
//
//   - `bodyHost`: the DOM node (in ScopedView's right-column content) to
//     portal the chat body (transcript + canvas) into. ChatConsole
//     renders docked when set.
//   - `chatBar`: the chat's tab model — { tabs, onReorder, onNew,
//     onExportPdf, pdfExporting, hasItems, canvasOpen } — so ScopedPage
//     can render the chat's tabs on the rail (next to the mode switch)
//     and auto-expand the chat column when the canvas has items, without
//     owning ChatConsole's state.
//
// Subscribers select narrowly: ChatConsole watches `bodyHost` only (via
// getDockBodyHost) so it does NOT re-render when chatBar churns —
// otherwise its own chatBar publish would loop. ScopedPage watches
// `chatBar` (via getDockChatBar). `getDock*` return stable refs between
// changes (useSyncExternalStore compares with Object.is).

let state = { bodyHost: null, chatBar: null, columnExpanded: false, dockAvailable: false, hostPage: false };
const subscribers = new Set();

function notify() {
  subscribers.forEach((fn) => fn());
}

export function getDock() {
  return state;
}

export function getDockBodyHost() {
  return state.bodyHost;
}

export function getDockChatBar() {
  return state.chatBar;
}

// Whether the chat column is in two-column (expanded) mode. Published by
// ScopedPage (which owns the expand state); ChatConsole reads it to gate
// the canvas — the canvas only renders when there's room for it.
export function getDockColumnExpanded() {
  return state.columnExpanded;
}

export function setDockColumnExpanded(expanded) {
  const next = !!expanded;
  if (next === state.columnExpanded) return;
  state = { ...state, columnExpanded: next };
  notify();
}

export function setDockBodyHost(host) {
  const next = host || null;
  if (next === state.bodyHost) return;
  state = { ...state, bodyHost: next };
  notify();
}

export function setChatBar(chatBar) {
  const next = chatBar || null;
  if (next === state.chatBar) return;
  state = { ...state, chatBar: next };
  notify();
}

// Whether a dock host page (ScopedPage / Explore) is currently mounted,
// so ⌘K can surface the docked chat (flip the right column to Chat +
// un-collapse it) instead of the standalone overlay. Read imperatively
// by ChatConsole's hotkey; set by ScopedPage on mount/unmount.
export function getDockAvailable() {
  return state.dockAvailable;
}

export function setDockAvailable(available) {
  const next = !!available;
  if (next === state.dockAvailable) return;
  state = { ...state, dockAvailable: next };
  notify();
}

// Whether a page that can host the dock is on screen at all, open column or
// not: dockAvailable says the column is open for the hotkey to use; this says
// an activation could open it. Read by surfaces that bring a chat to the page
// (the Reviews lens's "Open the transcript"); set by the dock host pages
// while visible.
export function getDockHostPage() {
  return state.hostPage;
}

export function setDockHostPage(present) {
  const next = !!present;
  if (next === state.hostPage) return;
  state = { ...state, hostPage: next };
  notify();
}

export function subscribeDock(callback) {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

export function resetDockIdentityState() {
  state = { bodyHost: null, chatBar: null, columnExpanded: false, dockAvailable: false, hostPage: false };
  notify();
}
